// Moved from `src/hooks/mutation/useMutateCreateTeamInvitationLink.ts` (now the
// app delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Three app couplings severed per ARCFE-921: the success toast, the destructive
// toast (which reads the backend `detail.msg`), and the Rematch
// `analyticsScribe.trackSendTeamInvitation` dispatch. A package may never
// import the store or dispatch — `no-new-rematch-coupling` is a hard block —
// so the analytics payload crosses `onInvitationCreated` and the delegate
// dispatches. `teamName` was a hook ARGUMENT used only for that payload, so it
// leaves the package surface entirely.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS, type CreateInvitationResponse } from '../../contracts';
import {
  createTeamInvitationLink,
  createTeamInvitationLinkWithRoleIds,
} from '../api/teams';
import { isTransportError, type WorkspaceHttpError } from '../config';

export interface CreateTeamInvitationLinkVariables {
  teamId: string;
  email: string;
  roleIds?: string[];
}

export interface UseMutateCreateTeamInvitationLinkOptions {
  /**
   * App-reaction port: fired where the legacy success toast + Rematch
   * `trackSendTeamInvitation` dispatch fired, in that order.
   */
  onInvitationCreated?: (result: {
    variables: CreateTeamInvitationLinkVariables;
    invitationLink: CreateInvitationResponse['invitation_link'];
  }) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onInvitationError?: (error: WorkspaceHttpError) => void;
}

/** POST /teams/{team_id}/invitations */
export function useMutateCreateTeamInvitationLink(
  options?: UseMutateCreateTeamInvitationLinkOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, email, roleIds }: CreateTeamInvitationLinkVariables) => {
      if (roleIds) {
        const data = await createTeamInvitationLinkWithRoleIds(teamId, email, roleIds);
        return data;
      } else {
        const data = await createTeamInvitationLink(teamId, email);
        return data;
      }
    },
    onSuccess: ({ data }, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: variables.teamId }],
      });
      options?.onInvitationCreated?.({
        variables,
        invitationLink: data.invitation_link,
      });
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onInvitationError?.(error);
      }
    },
  });
}
